/*
 * Copyright (c) Dito Technologies
 */

import React, {Dispatch} from "react";
import UseStaffSelectAvailable from "ui/feature/root/staff-select/UseStaffSelectAvailable";
import {Action, SetStaffSelectStepStatusAction, State, StepStatus} from "ui/feature/root/FormReducer";
import CompletePreviousStepsSection from "ui/feature/root/common/CompletePreviousStepsSection";
import UsePollStaffSelectSteps from "ui/feature/root/staff-select/UsePollStaffSelectSteps";
import ProcessingSection from "ui/feature/root/common/ProcessingSection";
import StaffSelectPage from "ui/feature/root/staff-select/StaffSelectPage";
import Body from "ui/kit/typography/Body";
import styles from "./StaffSelectWorkspace.module.scss";
import {createFragmentContainer} from "react-relay";
import {graphql} from "babel-plugin-relay/macro";
import {
    StaffSelectWorkspace_piece$data
} from "ui/feature/root/staff-select/__generated__/StaffSelectWorkspace_piece.graphql";

interface Props {
    state: State;
    dispatch: Dispatch<Action>;
    piece: StaffSelectWorkspace_piece$data | null;
    retry: (() => void) | null;
}

const StaffSelectWorkspace: React.FC<Props> = ({state, dispatch, piece, retry}) => {
    const staffSelectAvailable = UseStaffSelectAvailable(state);
    UsePollStaffSelectSteps(state, piece, retry);

    // make sure that the state of the staff is correct.
    React.useEffect(() => {
        if (!piece) {
            return;
        }
        const staffPages = piece.sheets;
        if (staffPages) {
            let totalNumberOfPagesSuccess = 0;
            for (const page of staffPages) {
                const isSuccess = state.barFinderUserConfirmationSuccessPageIds.includes(page.id)
                if (isSuccess) {
                    totalNumberOfPagesSuccess += 1
                }
            }
            if (totalNumberOfPagesSuccess === 0) {
                const action: SetStaffSelectStepStatusAction = {
                    type: "set_staff_select_step_status",
                    status: StepStatus.NEEDS_EDIT
                }
                dispatch(action);
            } else if (totalNumberOfPagesSuccess === staffPages.length) {
                const action: SetStaffSelectStepStatusAction = {
                    type: "set_staff_select_step_status",
                    status: StepStatus.SUCCESS
                }
                dispatch(action);
            } else {
                const action: SetStaffSelectStepStatusAction = {
                    type: "set_staff_select_step_status",
                    status: StepStatus.IN_PROGRESS
                }
                dispatch(action);
            }
        } else {
            // no staff pages
            const action: SetStaffSelectStepStatusAction = {
                type: "set_staff_select_step_status",
                status: StepStatus.NEEDS_EDIT
            }
            dispatch(action);
        }
    }, [dispatch, piece, state.barFinderUserConfirmationSuccessPageIds]);

    if (staffSelectAvailable) {
        if (!piece || !piece.sheets) {
            return <ProcessingSection/>;
        }
        return (
            <div className={styles.container}>
                <Body className={styles.bodyText}>Ensure that we found all the correct staffs in your music piece.
                    Please delete staffs that are incorrect. It is okay if the staff we found does not expand the whole
                    width. If you did not get good results, retake the picture again with the page as flat as
                    possible.</Body>
                {piece.sheets.map((page) => <StaffSelectPage state={state} key={page.id}
                                                           sheet={page}
                                                           dispatch={dispatch}/>)}
            </div>
        );
    } else {
        return (
            <CompletePreviousStepsSection/>
        );
    }
}

export default createFragmentContainer(StaffSelectWorkspace, {
    piece: graphql`
    fragment StaffSelectWorkspace_piece on MusicPiece {
        id
        sheets {
            id
            ...StaffSelectPage_sheet
            bar_finder_steps: steps(step_type: BAR_FINDER) {
                id 
                status
            }
        }
    }
    `
});

